import React from "react";
import { Activity, Bell, TrendingUp, Map, MessageSquare, Calendar, Droplet, Zap, Smartphone, Target, Leaf, Users } from "lucide-react";
import "../style/home.css";

const Home = () => {
    return (
        <div className="home-container">
            <header className="header">
                <div className="header-content">
                    <h1 className="logo">
                        <img src="/img/logo.png" alt="AgroNexus Logo" className="logo-img" />
                        AgroNexus
                    </h1>
                    <nav className="nav">
                        <a href="#features">Características</a>
                        <a href="#impact">Impacto</a>
                        <a href="#benefits">Beneficios</a>
                        <a href="#cta">Comenzar</a>
                        <a href="/login" className="login-btn">Iniciar sesión</a>
                    </nav>
                </div>
            </header>

            <section className="hero">
                <div className="hero-content">
                    <h2>Tecnología Inteligente para Agricultura Moderna</h2>
                    <p>
                        Monitorea, gestiona y optimiza tus cultivos con nuestra plataforma integral.
                        Sensores IoT, análisis en tiempo real y herramientas colaborativas diseñadas
                        específicamente para agricultores.
                    </p>
                    <a href="#features" className="btn-primary">Descubre cómo funciona</a>
                </div>
            </section>

            <section id="features" className="section">
                <h3>Sistema Completo de Monitoreo</h3>
                <p className="section-subtitle">
                    Todo lo que necesitas para tomar el control total de tu producción agrícola
                </p>
                <div className="cards">
                    <div className="card">
                        <div className="card-icon"><Activity /></div>
                        <h4>Monitoreo en Tiempo Real</h4>
                        <p>Datos instantáneos de temperatura, humedad, luz UV, pH del suelo y más. Visualiza todo desde tu dispositivo.</p>
                    </div>
                    <div className="card">
                        <div className="card-icon"><Bell /></div>
                        <h4>Alertas Inteligentes</h4>
                        <p>Notificaciones automáticas ante condiciones críticas: sequías, heladas, plagas o sobrecalentamiento.</p>
                    </div>
                    <div className="card">
                        <div className="card-icon"><TrendingUp /></div>
                        <h4>Análisis Histórico</h4>
                        <p>Gráficos detallados de evolución y tendencias para decisiones basadas en datos concretos.</p>
                    </div>
                    <div className="card">
                        <div className="card-icon"><Map /></div>
                        <h4>Gestión de Terrenos</h4>
                        <p>Agrupa múltiples sensores por zonas, comparte terrenos con colaboradores y gestiona permisos personalizados.</p>
                    </div>
                    <div className="card">
                        <div className="card-icon"><MessageSquare /></div>
                        <h4>Comunicación Integrada</h4>
                        <p>Chats directos y grupales para coordinar equipos, compartir observaciones y resolver problemas al instante.</p>
                    </div>
                    <div className="card">
                        <div className="card-icon"><Calendar /></div>
                        <h4>Agenda y Tareas</h4>
                        <p>Planifica riego, fertilización, fumigaciones y cosechas. Recibe recordatorios y nunca pierdas una tarea importante.</p>
                    </div>
                </div>
            </section>

            <section className="impact-section" id="impact">
                <h3>Diseñado Para Transformar la Agricultura</h3>
                <p className="section-subtitle">
                    AgroNexus nació de la necesidad de brindar a los agricultores herramientas
                    tecnológicas accesibles que realmente impacten su productividad y calidad de vida
                </p>
                <div className="impact-grid">
                    <div className="impact-card">
                        <div className="impact-number">40%</div>
                        <div className="impact-label">Ahorro en agua</div>
                    </div>
                    <div className="impact-card">
                        <div className="impact-number">35%</div>
                        <div className="impact-label">Aumento en producción</div>
                    </div>
                    <div className="impact-card">
                        <div className="impact-number">60%</div>
                        <div className="impact-label">Reducción de pérdidas</div>
                    </div>
                    <div className="impact-card">
                        <div className="impact-number">24/7</div>
                        <div className="impact-label">Monitoreo continuo</div>
                    </div>
                </div>
            </section>

            <section className="section">
                <h3>Funcionalidades Avanzadas</h3>
                <p className="section-subtitle">
                    Herramientas profesionales pensadas para agricultores modernos
                </p>
                <div className="features-detailed">
                    <div className="feature-detailed">
                        <h4><Map size={24} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} /> Gestión Colaborativa</h4>
                        <ul>
                            <li>Comparte terrenos con socios o empleados</li>
                            <li>Asigna permisos: solo lectura, edición o administración</li>
                            <li>Gestiona múltiples fincas desde una cuenta</li>
                            <li>Historial de cambios y acciones por usuario</li>
                        </ul>
                    </div>
                    <div className="feature-detailed">
                        <h4><MessageSquare size={24} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} /> Comunicación en Equipo</h4>
                        <ul>
                            <li>Chats directos con colaboradores</li>
                            <li>Grupos por terreno o equipo de trabajo</li>
                            <li>Comparte fotos, reportes y observaciones</li>
                            <li>Notificaciones instantáneas</li>
                        </ul>
                    </div>
                    <div className="feature-detailed">
                        <h4><Calendar size={24} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} /> Planificación Inteligente</h4>
                        <ul>
                            <li>Agenda eventos de riego, fertilización y cosecha</li>
                            <li>Crea tareas y asígnalas a tu equipo</li>
                            <li>Recordatorios automáticos por email o SMS</li>
                            <li>Integración con calendarios externos</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="benefits" id="benefits">
                <h3>¿Por Qué Elegir AgroNexus?</h3>
                <p className="section-subtitle">
                    Impacto real en tu producción, rentabilidad y tranquilidad
                </p>
                <div className="benefits-grid">
                    <div className="benefit-item">
                        <div className="benefit-icon"><Droplet /></div>
                        <div className="benefit-content">
                            <h4>Optimización de Recursos</h4>
                            <p>Usa agua y fertilizantes solo cuando y donde se necesitan, reduciendo costos hasta un 40%.</p>
                        </div>
                    </div>
                    <div className="benefit-item">
                        <div className="benefit-icon"><Zap /></div>
                        <div className="benefit-content">
                            <h4>Prevención de Pérdidas</h4>
                            <p>Detecta problemas antes de que se conviertan en crisis. Evita pérdidas por clima, plagas o enfermedades.</p>
                        </div>
                    </div>
                    <div className="benefit-item">
                        <div className="benefit-icon"><Smartphone /></div>
                        <div className="benefit-content">
                            <h4>Acceso Universal</h4>
                            <p>Monitorea desde cualquier dispositivo: celular, tablet o computadora. Internet o datos móviles.</p>
                        </div>
                    </div>
                    <div className="benefit-item">
                        <div className="benefit-icon"><Target /></div>
                        <div className="benefit-content">
                            <h4>Decisiones Basadas en Datos</h4>
                            <p>Deja de adivinar. Cada acción respaldada por información precisa y análisis históricos.</p>
                        </div>
                    </div>
                    <div className="benefit-item">
                        <div className="benefit-icon"><Leaf /></div>
                        <div className="benefit-content">
                            <h4>Mejor Calidad de Cultivos</h4>
                            <p>Condiciones óptimas = productos de mayor calidad y mejor precio en el mercado.</p>
                        </div>
                    </div>
                    <div className="benefit-item">
                        <div className="benefit-icon"><Users /></div>
                        <div className="benefit-content">
                            <h4>Trabajo en Equipo</h4>
                            <p>Coordina eficientemente con tu equipo, comparte información y toma decisiones colaborativas.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="cta-section" id="cta">
                <div className="cta-content">
                    <h3>¿Listo Para Revolucionar Tu Campo?</h3>
                    <p>
                        Únete a cientos de agricultores que ya transformaron su forma de trabajar.
                        Comienza gratis y descubre el poder de la agricultura inteligente.
                    </p>
                    <a href="/registro" className="btn-primary">Crear cuenta gratuita</a>
                </div>
            </section>

            <footer className="footer">
                <p>© {new Date().getFullYear()} AgroNexus. Innovación para el campo | Todos los derechos reservados.</p>
            </footer>
        </div>
    );
};

export default Home;